'use client';

import { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, CheckIcon, ArrowLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import NotebookCard from '../shared/notebook-card';
import axios from 'axios';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { CaretDown } from '@phosphor-icons/react';
import styled from 'styled-components';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import MyNotebooksLoader from '../../components/loaders/MyNotebookLoader';
import { useRef } from 'react';

const getTestTypes = async (packId) => {
  try {
    const response = await axios.get(`/api/packs/${packId}/testTypes`);

    // Validate response data
    const responseData = response.data.data || response.data;
    if (!Array.isArray(responseData)) {
      throw new Error('Invalid test types response format');
    }

    // Get unique test types
    const uniqueTypes = [...new Set(responseData)];
    const normalizedTypes = uniqueTypes.map((type, index) => ({
      id: type.toLowerCase().replace(/\s+/g, '-') || `type-${index}`,
      _id: type.toLowerCase().replace(/\s+/g, '-') || `type-${index}`,
      name: type || 'Unknown Test Type',
      title: type || 'Unknown Test Type',
      color: 'var(--q3-accent-normal)',
      icon: '/test-icon.svg',
    }));

    return normalizedTypes;
  } catch (error) {
    throw error;
  }
};

const getTestWiseNotebooks = async (packId, testType) => {
  try {
    const response = await axios.get(`/api/v2/notebooks/${packId}/test-wise`, {
      params: { testType },
    });

    // Handle the actual API response structure
    const responseData = response.data.data || response.data;

    // Check if the response has notebooks array directly or nested
    const notebooks = responseData.notebooks || responseData;

    if (!Array.isArray(notebooks)) {
      return [];
    }

    const normalizedNotebooks = notebooks.map((notebook, index) => ({
      id: notebook._id || notebook.id || `test-${index}`,
      _id: notebook._id || notebook.id || `test-${index}`,
      testId: notebook.testId || notebook._id || notebook.id,
      title: notebook.title || notebook.name || 'Untitled Test',
      name: notebook.title || notebook.name || 'Untitled Test',
      questionCount: notebook.questions ? notebook.questions.length : notebook.questionCount || 0,
      questions: notebook.questions || [],
      testType: testType,
      lastUsed: notebook.lastUsed || new Date().toISOString(),
      createdAt: notebook.createdAt || new Date().toISOString(),
      updatedAt: notebook.updatedAt || new Date().toISOString(),
      type: 'test',
      packId: packId,
      isArchived: false,
      isPinned: false,
    }));

    return normalizedNotebooks;
  } catch (error) {
    // If it's a 404 or similar, return empty array instead of throwing
    if (error.response?.status === 404) {
      return [];
    }

    throw error;
  }
};

export const TestWise = ({ onNotebookClick, onScreenChange, onBack, selectedPack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTestType, setActiveTestType] = useState(null);
  const [sortBy, setSortBy] = useState('Default');
  const [selectedTest, setSelectedTest] = useState('Tests');
  const [testTypes, setTestTypes] = useState([]);
  const [testNotebooks, setTestNotebooks] = useState({});
  const [loading, setLoading] = useState(false);
  const [testOptions, setTestOptions] = useState(['Tests']);
  const [error, setError] = useState(null);
  const tabsContainerRef = useRef(null);
  const scrollRef = useRef(null);
  const sortScrollRef = useRef(null);
  const [showScroll, setShowScroll] = useState(false);
  const [showSortScroll, setShowSortScroll] = useState(false);

  const sortOptions = ['Default', 'Last Used', 'Qs (High to Low)', 'Qs (Low to High)', 'A-Z', 'Z-A'];

  // Get pack ID from props or localStorage
  const packId = selectedPack?.id || localStorage.getItem('selectedPackId');

  useEffect(() => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.classList.add('scroll-hint');
      setTimeout(() => {
        tabsContainerRef.current.classList.remove('scroll-hint');
      }, 1500);
    }
  }, []);

  useEffect(() => {
    if (!packId) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const testTypesData = await getTestTypes(packId);

        setTestTypes(testTypesData);

        if (testTypesData.length > 0) {
          setActiveTestType(testTypesData[0].id);
          await fetchTestNotebooks(testTypesData[0].id, testTypesData[0].name);
        }
      } catch (err) {
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPack, packId]);

  // Check if scroll is needed - improved logic
  useEffect(() => {
    const checkScroll = () => {
      if (!scrollRef.current) return;

      const element = scrollRef.current;
      const hasOverflow = element.scrollWidth > element.clientWidth;
      setShowScroll(hasOverflow);
    };

    const checkSortScroll = () => {
      if (!sortScrollRef.current) return;

      const element = sortScrollRef.current;
      const hasOverflow = element.scrollWidth > element.clientWidth;
      setShowSortScroll(hasOverflow);
    };

    // Check scroll when testTypes change
    if (testTypes.length > 0) {
      // Use setTimeout to ensure DOM has been updated
      setTimeout(checkScroll, 100);
    }

    // Check sort scroll when sort options are rendered
    setTimeout(checkSortScroll, 100);

    // Also check on window resize
    const handleResize = () => {
      setTimeout(() => {
        checkScroll();
        checkSortScroll();
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [testTypes, sortOptions]);

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  const scrollSortRight = () => {
    if (sortScrollRef.current) {
      sortScrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const fetchTestNotebooks = async (testTypeId, testTypeName) => {
    try {
      setLoading(true);
      setError(null);

      const data = await getTestWiseNotebooks(packId, testTypeName);

      setTestNotebooks((prev) => {
        const newState = {
          ...prev,
          [testTypeId]: data,
        };

        return newState;
      });

      // Extract unique notebook titles from the notebooks
      const notebookTitles = [...new Set(data.map((n) => n.title))].filter(Boolean);

      setTestOptions(['Tests', ...notebookTitles]);
    } catch (err) {
      setError('Failed to load notebooks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTestTypeChange = async (testTypeId) => {
    setActiveTestType(testTypeId);
    setSelectedTest('Tests'); // Reset notebook filter when changing test type

    if (!testNotebooks[testTypeId]) {
      const testType = testTypes.find((t) => t.id === testTypeId);
      await fetchTestNotebooks(testTypeId, testType?.name);
    } else {
      // Update notebook title options for cached data
      const data = testNotebooks[testTypeId];
      const notebookTitles = [...new Set(data.map((n) => n.title))].filter(Boolean);
      setTestOptions(['Tests', ...notebookTitles]);
    }
  };

  const currentNotebooks = activeTestType ? testNotebooks[activeTestType] || [] : [];
  const filteredNotebooks = currentNotebooks.filter((notebook) =>
    notebook.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sortedNotebooks = [...filteredNotebooks].sort((a, b) => {
    switch (sortBy) {
      case 'Qs (High to Low)':
        return (b.questionCount || 0) - (a.questionCount || 0);
      case 'Qs (Low to High)':
        return (a.questionCount || 0) - (b.questionCount || 0);
      case 'A-Z':
        return a.title.localeCompare(b.title);
      case 'Z-A':
        return b.title.localeCompare(a.title);
      case 'Last Used':
        return new Date(b.lastUsed || b.updatedAt || 0) - new Date(a.lastUsed || a.updatedAt || 0);
      default:
        return 0;
    }
  });

  const displayedNotebooks =
    selectedTest !== 'Tests' ? sortedNotebooks.filter((notebook) => notebook.title === selectedTest) : sortedNotebooks;

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-2">Error Loading Data</h3>
          <p className="text-[var(--q3-neutral-light)] mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-white px-6 py-2 transition-colors duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const tabColor = 'var(--q3-accent-normal)';

  return (
    <div className="flex-1 p-4 md:p-6">
      {/* Mobile Header */}
      <div className="md:hidden mb-4">
        <div className="flex items-center justify-start gap-3 mb-2">
          {onBack && (
            <button onClick={onBack} className="p-2">
              <ArrowLeftIcon className="w-5 h-5 text-[var(--q3-neutral-default)]" />
            </button>
          )}
          <div className="text-left">
            <h1 className="text-xl font-bold text-[var(--q3-neutral-default)]">Test-wise Notebooks</h1>
            <p className="text-[10px] text-[var(--q3-neutral-light)]">All your bookmarked Qs categorized test wise</p>
          </div>
        </div>

        {/* Mobile Search Bar Below Title */}
        <div className="md:hidden mb-4">
          <div className="relative w-full">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--q3-neutral-light)] w-5 h-5" />
            <input
              type="text"
              placeholder="Search Notebook"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-full border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] placeholder-[var(--q3-neutral-light)] focus:outline-none focus:ring-2 focus:ring-[var(--q3-accent-normal)] focus:border-transparent text-sm"
            />
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden md:flex flex-col md:flex-row md:items-start justify-between gap-2 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[var(--q3-neutral-default)] mb-1">Test-wise Notebooks</h1>
          <p className="text-[var(--q3-neutral-light)]">All your bookmarked Qs categorized test wise</p>
        </div>

        {/* Search Bar */}
        <div className="ml-auto md:mt-2">
          <div className="relative w-52 md:w-60">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--q3-neutral-light)] w-5 h-5" />
            <input
              type="text"
              placeholder="Search Notebook"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-full border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] placeholder-[var(--q3-neutral-light)] focus:outline-none focus:ring-2 focus:ring-[var(--q3-accent-normal)] focus:border-transparent text-sm mr-4"
            />
          </div>
        </div>
      </div>

      <div className="mb-4 md:mb-6 relative">
        {/* Scrollable Tabs */}
        <div ref={scrollRef} className={`overflow-x-auto ${showScroll ? 'pr-8' : ''}`}>
          <TabsContainer color={tabColor} className="flex gap-4">
            {testTypes.map((testType) => (
              <Tab
                key={testType.id}
                isActive={activeTestType === testType.id}
                color={activeTestType === testType.id ? testType.color : 'var(--q3-neutral-light)'}
                tabColor={testType.color}
                onClick={() => handleTestTypeChange(testType.id)}
              >
                <p className="mb-0 text-base font-medium whitespace-nowrap">{testType.name}</p>
              </Tab>
            ))}
          </TabsContainer>
        </div>

        {/* Scroll Right Arrow — only when needed */}
        {showScroll && (
          <button
            onClick={scrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-[var(--q3-surface-default)] border rounded-full shadow-sm p-1 w-6 h-6 flex items-center justify-center hover:bg-[var(--q3-surface-dimmest)] transition-colors"
          >
            <CaretRightIcon size={16} className="text-[var(--q3-neutral-default)]" />
          </button>
        )}
      </div>

      {/* Filters and Sort */}
      <div className="w-full mb-10 relative">
        <div ref={sortScrollRef} className={`overflow-x-auto ${showSortScroll ? 'pr-8' : ''}`}>
          <div className="flex items-center gap-4 flex-nowrap min-w-fit">
            {/* Notebook Dropdown */}
            <div className="relative flex items-center flex-shrink-0">
              <Select value={selectedTest} onValueChange={setSelectedTest}>
                <SelectTrigger
                  className={`appearance-none text-sm rounded-full px-4 py-2 pr-4 text-[var(--q3-neutral-default)]  hover:bg-[var(--q3-surface-dimmest)] transition-colors cursor-pointer`}
                >
                  <SelectValue placeholder="Tests" />
                </SelectTrigger>

                <SelectContent>
                  {testOptions.map((title) => (
                    <SelectItem key={title} value={title}>
                      {title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Vertical Divider */}
            <div className="h-6 border-l border-[var(--q3-stroke-normal)]"></div>

            {/* Sort by text */}
            <span className="text-sm text-[var(--q3-neutral-default)] flex-shrink-0">Sort by</span>

            {/* Sort Options */}
            <div className="flex items-center gap-2 flex-nowrap">
              {sortOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => setSortBy(option)}
                  className={`cursor-pointer flex items-center gap-2 px-4 py-2 pr-4 rounded-full text-sm whitespace-nowrap flex-shrink-0 transition-colors
        ${
          sortBy === option
            ? 'bg-[var(--q3-accent-normal)] text-white'
            : 'bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] hover:bg-[var(--q3-surface-dimmest)] border border-[var(--q3-stroke-normal)]'
        }`}
                >
                  <span>{option}</span>
                  {sortBy === option && <CheckIcon size={14} weight="bold" />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Right Arrow for Sort Options — only when needed */}
        {showSortScroll && (
          <button
            onClick={scrollSortRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-[var(--q3-surface-default)] border rounded-full shadow-sm p-1 w-6 h-6 flex items-center justify-center hover:bg-[var(--q3-surface-dimmest)] transition-colors"
          >
            <CaretRightIcon size={16} className="text-[var(--q3-neutral-default)]" />
          </button>
        )}
      </div>

      {/* Loader or Notebooks Grid */}
      {loading ? (
        <MyNotebooksLoader />
      ) : displayedNotebooks.length > 0 ? (
        <div className="px-2 sm:px-4 md:px-6 lg:px-8 pb-6">
          <div className="flex justify-center sm:justify-start">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-3 sm:gap-3 md:gap-6">
              {displayedNotebooks.map((notebook) => (
                <NotebookCard
                  key={notebook.id}
                  title={notebook.title}
                  count={notebook.questionCount}
                  notebook={notebook}
                  onClick={() =>
                    onNotebookClick({
                      ...notebook,
                      type: 'test',
                      testId: notebook.testId,
                      questions: notebook.questions,
                      subject: testTypes.find((s) => s.id === activeTestType)?.name,
                    })
                  }
                  className="hover:scale-105 transition-transform duration-200"
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <MagnifyingGlassIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-2">😕 No notebooks found</h3>
          <p className="text-[var(--q3-neutral-light)] mb-6">
            {searchTerm || selectedTest !== 'Tests'
              ? 'Try adjusting your search or filter criteria'
              : 'No test-wise notebooks available for this test type'}
          </p>
          {(searchTerm || selectedTest !== 'Tests' || sortBy !== 'Default') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedTest('Tests');
                setSortBy('Default');
              }}
              className="rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-white px-6 py-2 transition-colors duration-300"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};
